import React, { forwardRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import {
  LpgCancelOutline,
  AlertErrorDefault,
  ActionCheckmarkCircleDefault,
  AlertInformationDefault,
  AlertWarningDefault,
} from "@/icons";

import { A11yText } from "../A11yText";
import { Icon } from "../Icon";

import styles from "./Notification.scss";

import NotificationHeadline from "./NotificationHeadline";
import NotificationLink from "./NotificationLink";
import NotificationCopyText from "./NotificationCopyText";

export const Notification = forwardRef(
  (
    {
      variant,
      customClass,
      headline,
      children,
      closeText,
      hide,
      closable,
      headlineProps,
      closeButtonProps,
      noMargins,
      noSideMargins,
      noTopMargin,
      noBottomMargin,
      ...restProps
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(true);

    useEffect(() => {
      if (hide !== undefined) {
        if (hide) {
          setIsOpen(false);
        } else {
          setIsOpen(true);
        }
      }
    }, [hide]);

    if (!isOpen) {
      return "";
    }

    const classNames = cx(styles.notification, customClass, {
      [styles.noMargins]: noMargins,
      [styles.noSideMargins]: noSideMargins,
      [styles.noTopMargin]: noTopMargin,
      [styles.noBottomMargin]: noBottomMargin,
    });
    return (
      <div className={styles.wrapper}>
        <div ref={ref} className={classNames} {...restProps}>
          <div
            className={`${styles["icon-container"]} ${variant.styles}`}
            data-testid="icon"
          >
            <span className={styles["icon-wrapper"]}>{variant.icon}</span>
          </div>
          <div className={`${styles.content}`}>
            {headline && (
              <Notification.Headline {...headlineProps}>
                {headline}
              </Notification.Headline>
            )}
            {children}
          </div>
          {hide === undefined && closable === true && (
            <button
              className={styles.close}
              type="button"
              onClick={() => setIsOpen(false)}
              {...closeButtonProps}
            >
              <Icon icon={LpgCancelOutline} size={Icon.Size.medium} />
              <A11yText>{closeText}</A11yText>
            </button>
          )}
        </div>
      </div>
    );
  }
);

Notification.Headline = NotificationHeadline;
Notification.Link = NotificationLink;
Notification.CopyText = NotificationCopyText;

Notification.Variant = {
  information: {
    icon: (
      <Icon
        icon={AlertInformationDefault}
        color="#FFFFFF"
        size={Icon.Size.parent}
      />
    ),
    styles: styles.information,
  },
  confirmation: {
    icon: (
      <Icon
        icon={ActionCheckmarkCircleDefault}
        color="#FFFFFF"
        size={Icon.Size.parent}
      />
    ),
    styles: styles.confirmation,
  },
  warning: {
    icon: (
      <Icon icon={AlertWarningDefault} color="black" size={Icon.Size.parent} />
    ),
    styles: styles.warning,
  },
  alert: {
    icon: (
      <Icon icon={AlertErrorDefault} color="white" size={Icon.Size.parent} />
    ),
    styles: styles.alert,
  },
};

Notification.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  variant: PropTypes.shape({
    icon: PropTypes.node,
    styles: PropTypes.string,
  }),
  customClass: PropTypes.string,
  closeButtonProps: PropTypes.shape({}),
  closeText: PropTypes.string,
  hide: PropTypes.bool,
  closable: PropTypes.bool,
  /**
   * @deprecated use <Notification.Headline> in children instead.
   */
  headline: PropTypes.string,
  headlineProps: PropTypes.shape({}),
  noMargins: PropTypes.bool,
  noSideMargins: PropTypes.bool,
  noTopMargin: PropTypes.bool,
  noBottomMargin: PropTypes.bool,
};

Notification.defaultProps = {
  variant: Notification.Variant.information,
  customClass: undefined,
  closeText: "Schließen",
  hide: undefined,
  closable: true,
  headline: undefined,
  headlineProps: {},
  closeButtonProps: {},
  noMargins: false,
  noSideMargins: false,
  noTopMargin: false,
  noBottomMargin: false,
};

Notification.displayName = "Notification";

export default Notification;
